import { sanitizeHtml } from "../utils/sanitize";
import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog.jsx';
import RichTextEditor from '../components/RichTextEditor';
import { Mail, Edit, RotateCcw, Eye, Save, X, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function SystemEmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // template key being edited
  const [editData, setEditData] = useState({ subject: '', body: '' });
  const [editorMode, setEditorMode] = useState('visual');
  const [saving, setSaving] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = async () => {
    try {
      const res = await api.get('/system-templates');
      setTemplates(res.data);
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  };

  const handleEdit = (tpl) => {
    setEditing(tpl.key);
    setEditData({ subject: tpl.subject, body: tpl.body });
    setEditorMode('visual');
    setPreviewData(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/system-templates/${editing}`, editData);
      toast.success('Template saved');
      setEditing(null);
      loadTemplates();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleReset = async (key) => {
    if (!window.confirm('Reset this template to its default? Your customizations will be removed.')) return;
    try {
      await api.post(`/system-templates/${key}/reset`);
      toast.success('Template reset to default');
      loadTemplates();
    } catch { toast.error('Failed to reset'); }
  };

  const handlePreview = async () => {
    try {
      const res = await api.post(`/system-templates/${editing}/preview`, editData);
      setPreviewData(res.data);
    } catch (err) {
      toast.error('Preview failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;

  // Group by category
  const categories = {};
  templates.forEach(t => { const c = t.category || 'Other'; if (!categories[c]) categories[c] = []; categories[c].push(t); });

  return (
    <div className="co-page co-scope co-settings space-y-6" data-testid="system-email-templates-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2"><Mail className="h-7 w-7" /> Email Templates</h1>
        <p className="text-gray-500 mt-1">Edit every email CipherOne sends to staff and clients</p>
      </div>

      {Object.entries(categories).map(([category, tpls]) => (
        <div key={category}>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">{category}</h2>
          <div className="space-y-2">
            {tpls.map(tpl => (
              <Card key={tpl.key} data-testid={`template-card-${tpl.key}`}>
                <CardContent className="p-4 flex items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">{tpl.name}</span>
                      {tpl.is_customized && <Badge variant="secondary" className="text-xs">Customized</Badge>}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{tpl.description}</p>
                    <p className="text-xs text-gray-400 mt-1 font-mono truncate">Subject: {tpl.subject}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => handleEdit(tpl)} data-testid={`edit-template-${tpl.key}`}>
                      <Edit className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                    {tpl.is_customized && (
                      <Button size="sm" variant="ghost" onClick={() => handleReset(tpl.key)} title="Reset to default">
                        <RotateCcw className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}

      {/* Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) setEditing(null); }}>
        <DialogContent className="co-scope co-settings max-w-5xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit: {templates.find(t => t.key === editing)?.name}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">{templates.find(t => t.key === editing)?.description}</p>

              {/* Subject */}
              <div>
                <Label className="text-sm font-semibold">Subject Line</Label>
                <Input
                  data-testid="template-subject-input"
                  className="mt-1 font-mono text-sm"
                  value={editData.subject}
                  onChange={(e) => setEditData(prev => ({ ...prev, subject: e.target.value }))}
                />
              </div>

              {/* Body with Visual/HTML toggle */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-sm font-semibold">Email Body</Label>
                  <div className="flex items-center border rounded-md overflow-hidden text-xs">
                    <button type="button" className={`px-3 py-1 ${editorMode === 'visual' ? 'bg-gray-900 text-white' : 'bg-[#0b2130] text-[#b1c3ce] hover:bg-white/10'}`} onClick={() => setEditorMode('visual')}>Visual</button>
                    <button type="button" className={`px-3 py-1 ${editorMode === 'html' ? 'bg-gray-900 text-white' : 'bg-[#0b2130] text-[#b1c3ce] hover:bg-white/10'}`} onClick={() => setEditorMode('html')}>HTML</button>
                  </div>
                </div>
                {editorMode === 'visual' ? (
                  <RichTextEditor
                    key={`tpl-${editing}`}
                    content={editData.body}
                    onChange={(html) => setEditData(prev => ({ ...prev, body: html }))}
                    placeholder="Compose your email template..."
                    minHeight="260px"
                  />
                ) : (
                  <Textarea
                    data-testid="template-body-textarea"
                    className="min-h-[300px] font-mono text-xs"
                    value={editData.body}
                    onChange={(e) => setEditData(prev => ({ ...prev, body: e.target.value }))}
                  />
                )}
              </div>

              {/* Variables */}
              <div>
                <Label className="text-sm font-semibold mb-1 block">Available Variables</Label>
                <div className="flex flex-wrap gap-1.5">
                  {(templates.find(t => t.key === editing)?.variables || []).map(v => (
                    <Badge
                      key={v.name}
                      variant="secondary"
                      className="cursor-pointer text-xs font-mono hover:bg-blue-100"
                      title={v.desc}
                      onClick={() => { navigator.clipboard.writeText(`{{${v.name}}}`); toast.success(`Copied {{${v.name}}}`); }}
                    >
                      {'{{' + v.name + '}}'}
                    </Badge>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">Click to copy. These are replaced with real data when the email is sent.</p>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t">
                <Button onClick={handleSave} disabled={saving} data-testid="save-template-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                  Save
                </Button>
                <Button variant="outline" onClick={handlePreview} data-testid="preview-template-btn">
                  <Eye className="h-4 w-4 mr-1" /> Preview
                </Button>
                <Button variant="ghost" onClick={() => setEditing(null)}><X className="h-4 w-4 mr-1" /> Cancel</Button>
              </div>

              {/* Preview */}
              {previewData && (
                <div className="border rounded-lg overflow-hidden mt-2">
                  <div className="bg-gray-50 px-4 py-2 border-b flex items-center justify-between">
                    <span className="text-sm font-medium">Preview (Sample Data)</span>
                    <Button variant="ghost" size="sm" className="h-6" onClick={() => setPreviewData(null)}><X className="h-3 w-3" /></Button>
                  </div>
                  <div className="p-3 bg-gray-50 text-sm"><strong>Subject:</strong> {previewData.subject}</div>
                  <div className="p-4 bg-white" dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewData.body_html) }} />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
